/**
 * Lovasok Marketplace - Chrome bundle (minden oldal)
 *
 * Csak az Alpine core + collapse plugin: a téma header (mobil menü, kereső
 * toggle) inline x-data-t használ, ezért ez globális. A nevesített Alpine
 * komponensek az alpine-app.min.js-ben vannak, az alpine:init eseményben
 * regisztrálnak — plugin-oldalakon az app bundle ennek a fájlnak a kiírása
 * ELŐTT töltődik, hogy a regisztráció az Alpine.start() előtt lefusson.
 *
 * @package Lovasok_Marketplace
 */

import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import { LovasokAPI } from './utils/api.js';
import { formatPrice } from './utils/formatters.js';

Alpine.plugin(collapse);

Alpine.data('headerChrome', (config = {}) => ({
    mobileMenuOpen: false,
    searchOpen: false,
    searchIsMobile: false,
    searchQuery: config.initialQuery || '',
    suggestions: [],
    suggestionsLoading: false,
    suggestionsError: false,
    suggestionsSearched: false,
    activeSuggestion: -1,
    suggestionController: null,

    async loadSuggestions() {
        const query = this.searchQuery.trim();
        this.activeSuggestion = -1;

        if (this.suggestionController) {
            this.suggestionController.abort();
            this.suggestionController = null;
        }

        if (query.length < 2) {
            this.suggestions = [];
            this.suggestionsLoading = false;
            this.suggestionsError = false;
            this.suggestionsSearched = false;
            return;
        }

        const controller = new AbortController();
        this.suggestionController = controller;
        this.suggestionsLoading = true;
        this.suggestionsError = false;

        try {
            const data = await LovasokAPI.get(
                'horses',
                { search: query, per_page: 5, page: 1 },
                { signal: controller.signal }
            );

            if (controller.signal.aborted) return;
            this.suggestions = Array.isArray(data?.horses) ? data.horses : [];
            this.suggestionsSearched = true;
        } catch (error) {
            if (error?.name === 'AbortError') return;
            this.suggestions = [];
            this.suggestionsError = true;
            this.suggestionsSearched = true;
        } finally {
            if (this.suggestionController === controller) {
                this.suggestionController = null;
                this.suggestionsLoading = false;
            }
        }
    },

    moveSuggestion(direction) {
        if (!this.suggestions.length) return;
        this.activeSuggestion =
            (this.activeSuggestion + direction + this.suggestions.length) %
            this.suggestions.length;
        this.$nextTick(() => {
            document
                .getElementById(`lovasok-header-suggestion-${this.activeSuggestion}`)
                ?.scrollIntoView({ block: 'nearest' });
        });
    },

    chooseActiveSuggestion() {
        const horse = this.suggestions[this.activeSuggestion];
        if (!horse?.link) return false;
        window.location.assign(horse.link);
        return true;
    },

    toggleSearch() {
        if (this.searchOpen) {
            this.closeSearch(true);
            return;
        }

        this.searchIsMobile = window.matchMedia('(max-width: 639px)').matches;
        this.searchOpen = true;
        this.$nextTick(() => {
            const rect = this.$refs.headerSearchToggle?.getBoundingClientRect();
            if (rect) {
                this.$refs.headerSearchPanel?.style.setProperty(
                    '--lovasok-search-panel-top',
                    `${Math.round(rect.bottom + 8)}px`
                );
            }
            this.$refs.headerSearchInput?.focus();
            if (this.searchQuery.trim().length >= 2) {
                this.loadSuggestions();
            }
        });
    },

    closeSearch(restoreFocus = false) {
        this.searchOpen = false;
        this.activeSuggestion = -1;
        if (this.suggestionController) {
            this.suggestionController.abort();
            this.suggestionController = null;
        }
        if (restoreFocus) {
            this.$nextTick(() => this.$refs.headerSearchToggle?.focus());
        }
    },

    syncScrollLock() {
        const locked =
            this.mobileMenuOpen || (this.searchOpen && this.searchIsMobile);
        document.documentElement.classList.toggle('lovasok-scroll-locked', locked);
        document.body.classList.toggle('lovasok-scroll-locked', locked);
    },

    trapSearchFocus(event) {
        if (!this.searchIsMobile || event.key !== 'Tab') return;
        const focusable = Array.from(
            this.$refs.headerSearchPanel?.querySelectorAll(
                'button:not([disabled]), input:not([disabled]), a[href]'
            ) || []
        ).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    },

    formatSuggestionPrice(horse) {
        return formatPrice(
            horse?.price,
            true,
            horse?.price_status || horse?.priceStatus || '',
            horse?.priceCategory || '',
            horse?.currency || 'Ft'
        );
    },
}));

window.Alpine = Alpine;
window.LovasokAPI = LovasokAPI;

Alpine.start();
